import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentationType, MeetingLocationType, MeetingOnlineType } from '@models/documents/document-enums';
import { DocumentSelection } from '@models/documents/document-selection';
import { AnnualStatementSettingsData } from "@models/settings/annual-statement-settings-data";
import { DocumentSettingsData } from '@models/settings/document-settings-data';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  DocumentSelectionComponent
} from '@shared/components/documents-related/document-selection/document-selection.component';
import { ButtonComponent } from "@shared/controls/button/button.component";
import { AppCopyDirective } from "@shared/directives/copy.directive";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { DefaulValuePipe } from "@shared/pipes/defaultValuePipe";
import { IPartialHeaderData } from "@shared/services/header.service";
import { SettingsApiService } from '@shared/services/settings-api.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin, of, switchMap, tap } from 'rxjs';
import {
  EditDocumentSettingsModalComponent
} from "./edit-document-settings-modal/edit-document-settings-modal.component";
import {
  NotifyWhenAllPartiesSignedDocumentLabelRecord
} from "./edit-document-settings-modal/edit-document-settings-modal.constant";

@Component({
  selector: 'app-document-settings-page',
  standalone: true,
  imports: [
    ButtonComponent,
    LoaderComponent,
    AppCopyDirective,
    DefaulValuePipe,
    HasRoleDirective,
    DocumentSelectionComponent
  ],
  templateUrl: './document-settings-page.component.html',
  styleUrls: ['./document-settings-page.component.scss', '../../styles/settings-styles.scss']
})
export class DocumentSettingsPageComponent extends CommonPageComponent implements OnInit {
  private modalService = inject(NgbModal);
  private toast = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private settingsService = inject(SettingsApiService);

  override readonly headerData: IPartialHeaderData = {
    title: 'Document Settings',
    iconClass: 'ic-settings-20',
    backPath: ['/settings']
  };
  readonly NotifyWhenAllPartiesSignedDocumentLabelRecord = NotifyWhenAllPartiesSignedDocumentLabelRecord;
  readonly DocumentationType = DocumentationType;
  readonly MeetingLocationType = MeetingLocationType;
  readonly MeetingOnlineType = MeetingOnlineType;

  documentSettings = new DocumentSettingsData();
  asSettings = new AnnualStatementSettingsData();
  initDocumentSelectionFiles: DocumentSelection[] = [];

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadDocumentSettings();
  }

  openEditDocumentSettingsModal(): void {
    const modalRef = this.modalService.open(EditDocumentSettingsModalComponent, { size: 'lg' });
    const instance = modalRef.componentInstance as EditDocumentSettingsModalComponent;
    instance.documentSettings = this.documentSettings;
    instance.asSettings = this.asSettings;
    instance.initDocumentSelectionFiles = this.initDocumentSelectionFiles;

    modalRef.result.then(
      () => this.loadDocumentSettings(),
      () => {
      });
  }

  loadDocumentSettings(): void {
    this.isLoading.set(true);

    forkJoin({
      documentSettings: this.settingsService.getDocumentSettings(),
      asSettings: this.settingsService.getAnnualStatementSettings()
    })
      .pipe(
        tap(results => {
          this.documentSettings = results.documentSettings;
          this.asSettings = results.asSettings;
        }),
        switchMap(() =>
          this.settingsService.getAnnualStatementSelection().pipe(
            tap(selection => {
              this.initDocumentSelectionFiles = selection;
            })
          )
        ),
        catchError(() => {
          this.toast.error('Failed to load document or annual statement settings');
          return of(null);
        }),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
